import React, { useEffect, useState } from 'react';
import { Link, NavLink, useLocation, useNavigate } from 'react-router-dom';
import { Menu, X } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import NotificationBell from './NotificationBell';
import UserAvatar from './UserAvatar';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger,
} from './ui/dropdown-menu';
import { HP2, PHONE_DISPLAY, PHONE_HREF, SITE_SERVICES } from './homepage2/assets';
import { ServicesDropdown } from './ServicesDropdown';
import '../styles/homepage2.css';
import '../styles/homepage2-app.css';
import '../styles/approved/approved-app.css';

const NAV_LINKS = [
  { name: 'About Us', path: '/about' },
  { name: 'Contact Us', path: '/contact' },
  { name: 'Become A Guru', path: '/careers' },
];

const DASH_PATH = {
  admin: ['/admin/dashboard', 'Admin Dashboard'],
  master_admin: ['/admin/dashboard', 'Admin Dashboard'],
  cleaner: ['/cleaner/dashboard', 'Cleaner Portal'],
  client: ['/portal/dashboard', 'My Portal'],
};

// Approved Homepage 2.0 masthead — shared across all public pages.
const Header = () => {
  const [open, setOpen] = useState(false);
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => { setOpen(false); }, [location.pathname, location.hash]);
  useEffect(() => {
    document.body.style.overflow = open ? 'hidden' : '';
    return () => { document.body.style.overflow = ''; };
  }, [open]);

  const handleLogout = () => { logout(); navigate('/'); };
  const dash = user ? DASH_PATH[user.role] : null;

  return (
    <div className="hp2 hp2-masthead-root" data-testid="site-header">
      <header className="masthead">
        <Link className="identity" to="/" aria-label="Gleam Guru homepage" data-testid="header-logo">
          <img src={HP2.favicon} alt="Official Gleam Guru favicon" />
          <div className="wordmark mini-logo"><img src={HP2.logo} alt="Gleam Guru Cleaners" /></div>
        </Link>
        <nav className="nav" aria-label="Primary navigation">
          <NavLink className="home-link" to="/" end data-testid="nav-home">Home</NavLink>
          <ServicesDropdown />
          {NAV_LINKS.map((l) => (
            <NavLink key={l.path} to={l.path} data-testid={`nav-${l.name.toLowerCase().replace(/\s+/g, '-')}`}>{l.name}</NavLink>
          ))}
          {user && <NotificationBell />}
          {user ? (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button type="button" className="nav-account" data-testid="user-menu-trigger">
                  <UserAvatar user={user} size="sm" ring testId="header-avatar" />
                  <span>{user.full_name}</span>
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56" sideOffset={8}>
                <DropdownMenuLabel>
                  <p className="font-semibold text-zinc-900 text-sm truncate">{user.full_name}</p>
                  <p className="text-xs text-zinc-400 truncate">{user.email}</p>
                </DropdownMenuLabel>
                <DropdownMenuSeparator />
                {dash && <DropdownMenuItem onClick={() => navigate(dash[0])} className="cursor-pointer" data-testid="dashboard-link">{dash[1]}</DropdownMenuItem>}
                <DropdownMenuItem onClick={() => navigate('/account')} className="cursor-pointer" data-testid="account-settings-link">Account Settings</DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={handleLogout} className="cursor-pointer text-red-600 focus:text-red-600" data-testid="logout-button">Sign Out</DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          ) : (
            <Link className="nav-signin" to="/login" data-testid="login-button">Sign In</Link>
          )}
          <Link className="header-cta" to="/#quote-form" data-testid="get-quote-button">TALK WITH A GLEAM GURU</Link>
          <button
            type="button"
            className="menu-toggle"
            aria-label={open ? 'Close menu' : 'Open menu'}
            aria-expanded={open}
            onClick={() => setOpen((o) => !o)}
            data-testid="mobile-menu-toggle"
          >
            {open ? <X /> : <Menu />}
          </button>
        </nav>
      </header>

      {open && (
        <nav className="mobile-nav" aria-label="Mobile navigation" data-testid="mobile-nav">
          <Link to="/" data-testid="mobile-nav-home">Home</Link>
          <p className="mobile-group-label" id="mobile-services-label">Services</p>
          <div className="mobile-services" role="group" aria-labelledby="mobile-services-label">
            {SITE_SERVICES.map((s) => (
              <Link key={s.path} to={s.path} data-testid={`mobile-nav-service-${s.path.split('/').pop()}`}>{s.name}</Link>
            ))}
          </div>
          {NAV_LINKS.map((l) => (
            <Link key={l.path} to={l.path} data-testid={`mobile-nav-${l.name.toLowerCase().replace(/\s+/g, '-')}`}>{l.name}</Link>
          ))}
          {user ? (
            <>
              {dash && <Link to={dash[0]} data-testid="mobile-dashboard-link">{dash[1]}</Link>}
              <Link to="/account" data-testid="mobile-account-link">Account Settings</Link>
              <button type="button" className="mobile-signout" onClick={handleLogout} data-testid="mobile-logout-button">Sign Out</button>
            </>
          ) : (
            <Link to="/login" data-testid="mobile-sign-in-link">Sign In</Link>
          )}
          <Link className="mobile-cta" to="/#quote-form" data-testid="mobile-get-quote">TALK WITH A GLEAM GURU <span aria-hidden="true">↗</span></Link>
          <a className="mobile-phone" href={PHONE_HREF} data-testid="mobile-phone">Prefer to talk? {PHONE_DISPLAY}</a>
        </nav>
      )}
    </div>
  );
};

export default Header;
